import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";
import "./ValidationPortal.css";

const LABEL_PRESETS = {
    94107: {
        code: "94107",
        name: "Avery 94107 - 2 x 2 Waterproof (12)",
        cols: 3,
        rows: 4,
        labelW: 2.0,
        labelH: 2.0,
        marginTop: 0.58,
        marginLeft: 0.67,
        marginRight: 0.67,
        marginBottom: 0.5,
        gapX: 0.58,
        gapY: 0.6,
        qrSize: 112,
        codeFontRem: 0.58,
    },
    95271: {
        code: "95271",
        name: "Avery 95271 - 2.5 x 2.5 (9)",
        cols: 3,
        rows: 3,
        labelW: 2.5,
        labelH: 2.5,
        marginTop: 1.5,
        marginLeft: 0.25,
        marginRight: 0.25,
        marginBottom: 1.5,
        gapX: 0.25,
        gapY: 0.25,
        qrSize: 140,
        codeFontRem: 0.68,
    },
    18163: {
        code: "18163",
        name: "Avery 18163 - 2 x 4 (10)",
        cols: 2,
        rows: 5,
        labelW: 4.0,
        labelH: 2.0,
        marginTop: 0.5,
        marginLeft: 0.125,
        marginRight: 0.125,
        marginBottom: 0.5,
        gapX: 0.25,
        gapY: 0.0,
        qrSize: 112,
        codeFontRem: 0.7,
    },
};

const DEFAULT_LABEL_CODE = "94107";

function normalizeLocations(userDetail) {
    const explicitLocations = Array.isArray(userDetail?.accessibleLocations) ? userDetail.accessibleLocations : [];
    if (explicitLocations.length) {
        return explicitLocations
            .map((location) => ({
                id: Number(location.id),
                name: location.name || `Location ${location.id}`,
            }))
            .filter((location) => Number.isInteger(location.id) && location.id > 0);
    }

    const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
    const ids = Array.from(new Set(podiums.map((podium) => Number(podium.locationId)).filter((id) => Number.isInteger(id) && id > 0)));
    return ids.map((id) => ({ id, name: `Location ${id}` }));
}

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
        }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function getBatchTokensFromResponse(responseData) {
    const payload = responseData?.batch || null;
    if (!payload) return { batch: null, tokens: [] };

    const batch = payload?.batch || payload;
    const tokens = Array.isArray(payload?.tokens) ? payload.tokens : [];

    return {
        batch,
        tokens,
    };
}

const ValidationTenantsPage = () => {
    const { userDetail } = useContext(UserContext);
    const roleId = Number(userDetail?.roleId || 0);
    const isTenantPortal = Boolean(userDetail?.isTenantPortal);

    const accessibleLocations = useMemo(() => normalizeLocations(userDetail), [userDetail]);

    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [isLoadingTenants, setIsLoadingTenants] = useState(false);

    const [tenantForm, setTenantForm] = useState({
        name: "",
        contactName: "",
        contactEmail: "",
        portalUsername: "",
        portalPassword: "",
        portalIsActive: true,
        defaultCoverageMode: "full",
        defaultFixedAmountCents: "",
        isActive: true,
    });
    const [isSavingTenant, setIsSavingTenant] = useState(false);

    const [batchForm, setBatchForm] = useState({
        quantity: "12",
        coverageMode: "",
        fixedAmountCents: "",
        batchLabel: "",
    });
    const [labelCode, setLabelCode] = useState(DEFAULT_LABEL_CODE);
    const [isSavingBatch, setIsSavingBatch] = useState(false);
    const [lastCreatedBatch, setLastCreatedBatch] = useState(null);
    const printSheetsRef = useRef(null);

    const labelPreset = LABEL_PRESETS[labelCode] || LABEL_PRESETS[DEFAULT_LABEL_CODE];
    const labelsPerPage = labelPreset.cols * labelPreset.rows;

    const sheetStyle = useMemo(
        () => ({
            "--label-cols": String(labelPreset.cols),
            "--label-w": `${labelPreset.labelW}in`,
            "--label-h": `${labelPreset.labelH}in`,
            "--sheet-margin-top": `${labelPreset.marginTop}in`,
            "--sheet-margin-left": `${labelPreset.marginLeft}in`,
            "--sheet-margin-right": `${labelPreset.marginRight}in`,
            "--sheet-margin-bottom": `${labelPreset.marginBottom}in`,
            "--label-gap-x": `${labelPreset.gapX}in`,
            "--label-gap-y": `${labelPreset.gapY}in`,
            "--validation-code-font-size": `${labelPreset.codeFontRem}rem`,
        }),
        [labelPreset],
    );

    useEffect(() => {
        if (!accessibleLocations.length) {
            setSelectedLocationId("");
            return;
        }

        const preferredId = Number(userDetail?.locationId || accessibleLocations[0].id);
        const hasPreferred = accessibleLocations.some((location) => location.id === preferredId);
        setSelectedLocationId(String(hasPreferred ? preferredId : accessibleLocations[0].id));
    }, [accessibleLocations, userDetail]);

    useEffect(() => {
        async function loadTenants() {
            if (isTenantPortal) {
                try {
                    setIsLoadingTenants(true);
                    const res = await ValetApi.getTenantValidationProfile();
                    const tenant = res?.data?.profile || null;

                    if (!tenant?.id) {
                        setTenants([]);
                        setSelectedTenantId("");
                        return;
                    }

                    setSelectedLocationId(String(tenant.locationId || ""));
                    setTenants([tenant]);
                    setSelectedTenantId(String(tenant.id));
                } catch (err) {
                    setTenants([]);
                    setSelectedTenantId("");
                    toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load tenant profile", { autoClose: 1500 });
                } finally {
                    setIsLoadingTenants(false);
                }
                return;
            }

            if (!selectedLocationId) {
                setTenants([]);
                setSelectedTenantId("");
                return;
            }

            try {
                setIsLoadingTenants(true);
                const res = await ValetApi.listValidationTenantsByLocation(Number(selectedLocationId), false);
                const rows = Array.isArray(res?.data?.tenants) ? res.data.tenants : [];
                setTenants(rows);
                setSelectedTenantId((prev) => {
                    if (prev && rows.some((row) => Number(row.id) === Number(prev))) return prev;
                    return rows.length ? String(rows[0].id) : "";
                });
            } catch (err) {
                setTenants([]);
                setSelectedTenantId("");
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load validation tenants", { autoClose: 1500 });
            } finally {
                setIsLoadingTenants(false);
            }
        }

        loadTenants();
    }, [selectedLocationId, isTenantPortal]);

    async function refreshTenants(locationId, preferredTenantId = null) {
        const res = await ValetApi.listValidationTenantsByLocation(Number(locationId), false);
        const rows = Array.isArray(res?.data?.tenants) ? res.data.tenants : [];
        setTenants(rows);

        if (preferredTenantId && rows.some((row) => Number(row.id) === Number(preferredTenantId))) {
            setSelectedTenantId(String(preferredTenantId));
            return;
        }

        setSelectedTenantId((prev) => {
            if (prev && rows.some((row) => Number(row.id) === Number(prev))) return prev;
            return rows.length ? String(rows[0].id) : "";
        });
    }

    async function handleCreateTenant(evt) {
        evt.preventDefault();

        if (!selectedLocationId) {
            toast.error("Select a location first", { autoClose: 1200 });
            return;
        }

        const name = String(tenantForm.name || "").trim();
        if (!name) {
            toast.error("Tenant name is required", { autoClose: 1200 });
            return;
        }

        const mode = String(tenantForm.defaultCoverageMode || "full")
            .trim()
            .toLowerCase();
        const fixedAmountCents = mode === "fixed" ? Number(tenantForm.defaultFixedAmountCents) : null;
        const portalUsername = String(tenantForm.portalUsername || "")
            .trim()
            .toLowerCase();
        const portalPassword = String(tenantForm.portalPassword || "").trim();

        if (portalPassword && !portalUsername) {
            toast.error("Portal username is required when a portal password is provided", { autoClose: 1500 });
            return;
        }

        if (portalUsername && !portalPassword) {
            toast.error("Portal password is required when portal username is provided", { autoClose: 1500 });
            return;
        }

        if (portalPassword && portalPassword.length < 8) {
            toast.error("Portal password must be at least 8 characters", { autoClose: 1500 });
            return;
        }

        if (mode === "fixed" && (!Number.isInteger(fixedAmountCents) || fixedAmountCents <= 0)) {
            toast.error("Enter a valid default fixed amount in cents", { autoClose: 1400 });
            return;
        }

        try {
            setIsSavingTenant(true);
            const res = await ValetApi.createValidationTenant(Number(selectedLocationId), {
                name,
                contactName: String(tenantForm.contactName || "").trim(),
                contactEmail: String(tenantForm.contactEmail || "").trim(),
                portalUsername: portalUsername || null,
                portalPassword: portalPassword || null,
                portalIsActive: Boolean(tenantForm.portalIsActive),
                defaultCoverageMode: mode,
                defaultFixedAmountCents: mode === "fixed" ? fixedAmountCents : null,
                isActive: Boolean(tenantForm.isActive),
            });

            const created = res?.data?.tenant || null;
            setTenantForm({
                name: "",
                contactName: "",
                contactEmail: "",
                portalUsername: "",
                portalPassword: "",
                portalIsActive: true,
                defaultCoverageMode: "full",
                defaultFixedAmountCents: "",
                isActive: true,
            });
            await refreshTenants(Number(selectedLocationId), created?.id || null);
            toast.success("Validation tenant created", { autoClose: 1300 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to create tenant", { autoClose: 1500 });
        } finally {
            setIsSavingTenant(false);
        }
    }

    async function handleCreateBatch(evt) {
        evt.preventDefault();

        if (!selectedTenantId) {
            toast.error("Select a tenant first", { autoClose: 1200 });
            return;
        }

        const quantity = Number(batchForm.quantity);
        if (!Number.isInteger(quantity) || quantity <= 0 || quantity > 5000) {
            toast.error("Quantity must be between 1 and 5000", { autoClose: 1200 });
            return;
        }

        const coverageMode = String(batchForm.coverageMode || "")
            .trim()
            .toLowerCase();
        const fixedAmountCents = coverageMode === "fixed" ? Number(batchForm.fixedAmountCents) : null;
        if (coverageMode === "fixed" && (!Number.isInteger(fixedAmountCents) || fixedAmountCents <= 0)) {
            toast.error("Enter a valid fixed amount in cents", { autoClose: 1400 });
            return;
        }

        try {
            setIsSavingBatch(true);
            const payload = {
                quantity,
                coverageMode: coverageMode || undefined,
                fixedAmountCents: coverageMode === "fixed" ? fixedAmountCents : null,
                batchLabel: String(batchForm.batchLabel || "").trim(),
            };

            const res = isTenantPortal ? await ValetApi.createTenantValidationPrintBatch(payload) : await ValetApi.createValidationPrintBatch(Number(selectedTenantId), payload);

            const { batch, tokens } = getBatchTokensFromResponse(res?.data);
            setLastCreatedBatch({ batch, tokens });
            setBatchForm((prev) => ({ ...prev, batchLabel: "" }));
            toast.success(`Batch created with ${tokens.length} printable validations`, { autoClose: 1500 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to create validation batch", { autoClose: 1500 });
        } finally {
            setIsSavingBatch(false);
        }
    }

    function handlePrintLabels() {
        const sourceNode = printSheetsRef.current;
        if (!sourceNode) {
            toast.error("No labels are ready to print", { autoClose: 1200 });
            return;
        }

        const sheets = Array.from(sourceNode.querySelectorAll(".validation-ticket-sheet"));
        if (!sheets.length) {
            toast.error("No labels are ready to print", { autoClose: 1200 });
            return;
        }

        const printClass = "validation-print-labels-only";
        const htmlEl = document.documentElement;
        const bodyEl = document.body;

        let cleanedUp = false;
        let fallbackId = null;

        const cleanup = () => {
            if (cleanedUp) return;
            cleanedUp = true;
            htmlEl.classList.remove(printClass);
            bodyEl.classList.remove(printClass);
            if (fallbackId) window.clearTimeout(fallbackId);
        };

        htmlEl.classList.add(printClass);
        bodyEl.classList.add(printClass);

        window.addEventListener("afterprint", cleanup, { once: true });
        // Some browsers can miss afterprint in edge cases (cancel, preview close).
        fallbackId = window.setTimeout(cleanup, 2500);

        window.requestAnimationFrame(() => {
            window.print();
        });
    }

    if (!userDetail) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Tenant Setup</h2>
                    <p>Loading user permissions...</p>
                </div>
            </section>
        );
    }

    if (!isTenantPortal && roleId < 3) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Tenant Setup</h2>
                    <p>Manager access required.</p>
                </div>
            </section>
        );
    }

    const selectedTenant = tenants.find((tenant) => Number(tenant.id) === Number(selectedTenantId)) || null;
    const printableTokens = Array.isArray(lastCreatedBatch?.tokens) ? lastCreatedBatch.tokens : [];
    const tokenPages = [];

    for (let i = 0; i < printableTokens.length; i += labelsPerPage) {
        tokenPages.push(printableTokens.slice(i, i + labelsPerPage));
    }

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner validation-page-shell">
                <div className="validation-page-header">
                    <h2>{isTenantPortal ? "Validation Label Printing" : "Validation Tenant Setup and Label Printing"}</h2>
                    <p>{isTenantPortal ? "Generate and print single-use validation QR labels for your authorized team." : "Create tenants, issue single-use validation tokens, and print QR labels for desks and concierges."}</p>
                </div>

                {!isTenantPortal ? (
                    <div className="validation-toolbar-card">
                        <FormGroup>
                            <Label for="validationLocationId">Location</Label>
                            <Input
                                id="validationLocationId"
                                type="select"
                                value={selectedLocationId}
                                onChange={(evt) => setSelectedLocationId(evt.target.value)}>
                                {accessibleLocations.length ? null : <option value="">No locations available</option>}
                                {accessibleLocations.map((location) => (
                                    <option
                                        key={location.id}
                                        value={String(location.id)}>
                                        {location.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </div>
                ) : null}

                {!isTenantPortal ? (
                    <div className="validation-grid-two">
                        <div className="validation-panel">
                            <h3>Create Validation Tenant</h3>
                            <Form onSubmit={handleCreateTenant}>
                                <div className="validation-form-grid">
                                    <FormGroup>
                                        <Label for="tenantName">Tenant Name</Label>
                                        <Input
                                            id="tenantName"
                                            value={tenantForm.name}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, name: nextValue }));
                                            }}
                                            placeholder="Building HQ Front Desk"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="tenantContactName">Contact Name</Label>
                                        <Input
                                            id="tenantContactName"
                                            value={tenantForm.contactName}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, contactName: nextValue }));
                                            }}
                                            placeholder="Desk manager"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="tenantContactEmail">Contact Email</Label>
                                        <Input
                                            id="tenantContactEmail"
                                            type="email"
                                            value={tenantForm.contactEmail}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, contactEmail: nextValue }));
                                            }}
                                            placeholder="desk@client.com"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="tenantPortalUsername">Portal Username</Label>
                                        <Input
                                            id="tenantPortalUsername"
                                            value={tenantForm.portalUsername}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, portalUsername: nextValue }));
                                            }}
                                            placeholder="hq.frontdesk"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="tenantPortalPassword">Portal Password</Label>
                                        <Input
                                            id="tenantPortalPassword"
                                            type="password"
                                            value={tenantForm.portalPassword}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, portalPassword: nextValue }));
                                            }}
                                            placeholder="at least 8 characters"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="tenantCoverageMode">Default Coverage</Label>
                                        <Input
                                            id="tenantCoverageMode"
                                            type="select"
                                            value={tenantForm.defaultCoverageMode}
                                            onChange={(evt) => {
                                                const nextValue = evt.target.value;
                                                setTenantForm((prev) => ({ ...prev, defaultCoverageMode: nextValue }));
                                            }}>
                                            <option value="full">Full</option>
                                            <option value="half">Half</option>
                                            <option value="fixed">Fixed</option>
                                        </Input>
                                    </FormGroup>

                                    {tenantForm.defaultCoverageMode === "fixed" ? (
                                        <FormGroup>
                                            <Label for="tenantFixedAmount">Default Fixed Amount (cents)</Label>
                                            <Input
                                                id="tenantFixedAmount"
                                                type="number"
                                                min="1"
                                                value={tenantForm.defaultFixedAmountCents}
                                                onChange={(evt) => {
                                                    const nextValue = evt.target.value;
                                                    setTenantForm((prev) => ({ ...prev, defaultFixedAmountCents: nextValue }));
                                                }}
                                            />
                                        </FormGroup>
                                    ) : null}
                                </div>

                                <FormGroup
                                    check
                                    className="validation-toggle-row">
                                    <input
                                        id="tenantIsActive"
                                        type="checkbox"
                                        className="validation-toggle-checkbox"
                                        checked={Boolean(tenantForm.isActive)}
                                        onChange={(evt) => {
                                            const nextChecked = evt.target.checked;
                                            setTenantForm((prev) => ({ ...prev, isActive: nextChecked }));
                                        }}
                                    />
                                    <label
                                        className="form-check-label validation-toggle-label"
                                        htmlFor="tenantIsActive">
                                        Tenant active
                                    </label>
                                </FormGroup>

                                <FormGroup
                                    check
                                    className="validation-toggle-row">
                                    <input
                                        id="tenantPortalIsActive"
                                        type="checkbox"
                                        className="validation-toggle-checkbox"
                                        checked={Boolean(tenantForm.portalIsActive)}
                                        onChange={(evt) => {
                                            const nextChecked = evt.target.checked;
                                            setTenantForm((prev) => ({ ...prev, portalIsActive: nextChecked }));
                                        }}
                                    />
                                    <label
                                        className="form-check-label validation-toggle-label"
                                        htmlFor="tenantPortalIsActive">
                                        Portal login active
                                    </label>
                                </FormGroup>

                                <Button
                                    color="primary"
                                    type="submit"
                                    disabled={isSavingTenant || !selectedLocationId}>
                                    {isSavingTenant ? "Saving..." : "Create Tenant"}
                                </Button>
                            </Form>
                        </div>

                        <div className="validation-panel">
                            <h3>Tenants at This Location</h3>
                            {isLoadingTenants ? <p>Loading tenants...</p> : null}
                            {!isLoadingTenants && !tenants.length ? <p>No validation tenants configured yet.</p> : null}
                            {!isLoadingTenants && tenants.length ? (
                                <div className="validation-list-wrap">
                                    <table className="validation-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Default</th>
                                                <th>Contact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tenants.map((tenant) => (
                                                <tr
                                                    key={tenant.id}
                                                    className={Number(selectedTenantId) === Number(tenant.id) ? "is-selected" : ""}
                                                    onClick={() => setSelectedTenantId(String(tenant.id))}>
                                                    <td>{tenant.name}</td>
                                                    <td>
                                                        {tenant.defaultCoverageMode}
                                                        {tenant.defaultCoverageMode === "fixed" ? ` (${formatCents(tenant.defaultFixedAmountCents)})` : ""}
                                                    </td>
                                                    <td>{tenant.contactEmail || tenant.contactName || "--"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                <div className="validation-panel">
                    <h3>Create Printable Validation Batch</h3>
                    <Form onSubmit={handleCreateBatch}>
                        <div className="validation-form-grid">
                            {isTenantPortal ? (
                                <FormGroup>
                                    <Label for="batchTenantId">Tenant</Label>
                                    <Input
                                        id="batchTenantId"
                                        type="text"
                                        value={selectedTenant?.name || ""}
                                        readOnly
                                    />
                                </FormGroup>
                            ) : (
                                <FormGroup>
                                    <Label for="batchTenantId">Tenant</Label>
                                    <Input
                                        id="batchTenantId"
                                        type="select"
                                        value={selectedTenantId}
                                        onChange={(evt) => setSelectedTenantId(evt.target.value)}>
                                        {tenants.length ? null : <option value="">No tenants</option>}
                                        {tenants.map((tenant) => (
                                            <option
                                                key={tenant.id}
                                                value={String(tenant.id)}>
                                                {tenant.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )}

                            <FormGroup>
                                <Label for="batchQuantity">Quantity</Label>
                                <Input
                                    id="batchQuantity"
                                    type="number"
                                    min="1"
                                    max="5000"
                                    value={batchForm.quantity}
                                    onChange={(evt) => {
                                        const nextValue = evt.target.value;
                                        setBatchForm((prev) => ({ ...prev, quantity: nextValue }));
                                    }}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="batchCoverageMode">Coverage Override</Label>
                                <Input
                                    id="batchCoverageMode"
                                    type="select"
                                    value={batchForm.coverageMode}
                                    onChange={(evt) => {
                                        const nextValue = evt.target.value;
                                        setBatchForm((prev) => ({ ...prev, coverageMode: nextValue }));
                                    }}>
                                    <option value="">Use Tenant Default ({selectedTenant?.defaultCoverageMode || "full"})</option>
                                    <option value="full">Full</option>
                                    <option value="half">Half</option>
                                    <option value="fixed">Fixed</option>
                                </Input>
                            </FormGroup>

                            {batchForm.coverageMode === "fixed" ? (
                                <FormGroup>
                                    <Label for="batchFixedAmount">Fixed Amount (cents)</Label>
                                    <Input
                                        id="batchFixedAmount"
                                        type="number"
                                        min="1"
                                        value={batchForm.fixedAmountCents}
                                        onChange={(evt) => {
                                            const nextValue = evt.target.value;
                                            setBatchForm((prev) => ({ ...prev, fixedAmountCents: nextValue }));
                                        }}
                                    />
                                </FormGroup>
                            ) : null}

                            <FormGroup>
                                <Label for="batchLabel">Batch Label</Label>
                                <Input
                                    id="batchLabel"
                                    value={batchForm.batchLabel}
                                    onChange={(evt) => {
                                        const nextValue = evt.target.value;
                                        setBatchForm((prev) => ({ ...prev, batchLabel: nextValue }));
                                    }}
                                    placeholder="enter batch label"
                                />
                            </FormGroup>
                        </div>

                        <Button
                            color="primary"
                            type="submit"
                            disabled={isSavingBatch || !selectedTenantId}>
                            {isSavingBatch ? "Creating..." : "Create Print Batch"}
                        </Button>
                    </Form>
                </div>

                {lastCreatedBatch?.batch ? (
                    <div className="validation-panel validation-print-panel">
                        <div className="validation-print-header no-print">
                            <div>
                                <h3>Batch #{lastCreatedBatch.batch.id} Ready to Print</h3>
                                <p>
                                    {lastCreatedBatch.tokens.length} labels • Coverage: {lastCreatedBatch.batch.coverageMode}
                                    {lastCreatedBatch.batch.coverageMode === "fixed" ? ` (${formatCents(lastCreatedBatch.batch.fixedAmountCents)})` : ""}
                                </p>
                            </div>
                            <div className="validation-print-controls">
                                <FormGroup className="validation-print-control-item">
                                    <Label for="validationLabelCode">Avery Label Type</Label>
                                    <Input
                                        id="validationLabelCode"
                                        type="select"
                                        value={labelCode}
                                        onChange={(evt) => setLabelCode(evt.target.value)}>
                                        {Object.values(LABEL_PRESETS).map((preset) => (
                                            <option
                                                key={preset.code}
                                                value={preset.code}>
                                                {preset.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                            <Button
                                color="primary"
                                onClick={handlePrintLabels}>
                                Print Labels
                            </Button>
                        </div>

                        <div ref={printSheetsRef}>
                            {tokenPages.map((pageTokens, pageIdx) => (
                                <div
                                    key={`validation-sheet-${pageIdx}`}
                                    className="validation-ticket-sheet"
                                    style={sheetStyle}>
                                    {pageTokens.map((token) => (
                                        <div
                                            key={token.id || token.tokenCode}
                                            className="validation-print-card">
                                            <QRCodeSVG
                                                value={token.qrPayload || token.tokenCode}
                                                size={labelPreset.qrSize}
                                                level="H"
                                                fgColor="#000000"
                                                bgColor="#ffffff"
                                                marginSize={1}
                                            />
                                            <div className="validation-print-code">{token.tokenCode}</div>
                                            <div className="validation-print-sub">{selectedTenant?.name || "Validation"}</div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default ValidationTenantsPage;
